import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { CodeBlock, CodeBlockFrame } from '@sohanscript/lib/code/code-block';
import { getFileIcon } from '@sohanscript/lib/code/get-file-icon';
import { CopyButton } from '@sohanscript/ui/copy-button';
import { Label } from '@sohanscript/ui/label';
import { useImageInspector } from '../../use-image-inspector';
import { escapeAttribute, useCopyFeedback } from './image-inspector-shared';
import { InspectorSection } from './image-inspector-sidebar';

function Snippet({
	label,
	value,
	lang,
	filename,
}: {
	label: string;
	value: string;
	lang: string;
	filename: string;
}) {
	const copyFeedback = useCopyFeedback();
	const icon = getFileIcon({ language: lang, filename });

	return (
		<CodeBlockFrame
			header={
				<>
					{icon && <Iconify icon={icon} className="size-3.5 text-primary/60" />}
					<Label>{label}</Label>
				</>
			}
			actions={
				<CopyButton
					text={value}
					label={m.image_inspector_snippet_name({ label })}
					aria-label={m.image_inspector_copy_snippet_aria({ label })}
					{...copyFeedback}
				/>
			}
		>
			<CodeBlock
				code={value}
				lang={lang}
				className="text-2xs leading-relaxed select-all scrollbar-thin"
			/>
		</CodeBlockFrame>
	);
}

export function SnippetsSection({
	data,
	url,
}: {
	data: ReturnType<typeof useImageInspector>['data'];
	url: string;
}) {
	const markdown = `![${data.filename}](${url})`;
	const dimensions =
		data.width && data.height
			? ` width="${data.width}" height="${data.height}"`
			: '';
	const html = `<img src="${escapeAttribute(url)}" alt="${escapeAttribute(data.filename)}"${dimensions} />`;
	const css =
		data.width && data.height
			? `aspect-ratio: ${data.width} / ${data.height};`
			: null;

	return (
		<InspectorSection title={m.image_inspector_snippets()}>
			<div className="space-y-3">
				<Snippet
					label={m.image_inspector_markdown()}
					value={markdown}
					lang="md"
					filename="snippet.md"
				/>
				<Snippet
					label={m.image_inspector_html()}
					value={html}
					lang="html"
					filename="snippet.html"
				/>
				{css && (
					<Snippet
						label={m.image_inspector_css()}
						value={css}
						lang="css"
						filename="snippet.css"
					/>
				)}
			</div>
		</InspectorSection>
	);
}
